import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Modal } from './Modal';
import { Field, FormError, inputStyle } from './form';
import { btnGhost, btnPrimary } from './buttons';
import { creatorApi } from '../api/creator';
import type { ApiError } from '../api/client';
import type { PayoutMethodPublicDto, PayoutMethodType } from '../api/types';

const Schema = z
  .object({
    type: z.enum(['bank', 'paypal']),
    bankName: z.string().max(100).optional().or(z.literal('')),
    bankHolder: z.string().max(100).optional().or(z.literal('')),
    bankLast4: z.string().optional().or(z.literal('')),
    paypalEmail: z.string().optional().or(z.literal('')),
  })
  .superRefine((d, ctx) => {
    if (d.type === 'bank') {
      if (!d.bankName) ctx.addIssue({ code: 'custom', path: ['bankName'], message: 'Required' });
      if (!d.bankHolder) ctx.addIssue({ code: 'custom', path: ['bankHolder'], message: 'Required' });
      if (!/^\d{4}$/.test(d.bankLast4 ?? '')) ctx.addIssue({ code: 'custom', path: ['bankLast4'], message: 'Exactly 4 digits' });
    } else {
      if (!z.string().email().safeParse(d.paypalEmail).success) {
        ctx.addIssue({ code: 'custom', path: ['paypalEmail'], message: 'Valid email required' });
      }
    }
  });

type FormValues = z.infer<typeof Schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  current?: PayoutMethodPublicDto | null;
}

/**
 * Add/update payout destination (bank or PayPal). Shared by the Earnings
 * "Payout method" tab and the Settings screen. On save the backend re-flips
 * `verified=false` until an admin re-checks — the UI reflects that via the
 * "Pending verification" pill.
 */
export function PayoutMethodModal({ open, onClose, current }: Props) {
  const qc = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      type: (current?.type as PayoutMethodType) ?? 'bank',
      bankName: current?.bankName ?? '',
      bankHolder: current?.bankHolder ?? '',
      bankLast4: current?.bankLast4 ?? '',
      paypalEmail: current?.paypalEmail ?? '',
    },
  });
  const type = watch('type');

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      creatorApi.updatePayoutMethod(
        v.type === 'bank'
          ? { type: 'bank', bankName: v.bankName, bankHolder: v.bankHolder, bankLast4: v.bankLast4 }
          : { type: 'paypal', paypalEmail: v.paypalEmail },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creator', 'payout-method'] });
      reset();
      onClose();
    },
    onError: (err: ApiError) => setFormError(err?.message ?? 'Could not update payout method'),
  });

  const onSubmit = (vals: FormValues) => {
    const parsed = Schema.safeParse(vals);
    if (!parsed.success) {
      setFormError(parsed.error.errors[0]?.message ?? 'Invalid input');
      return;
    }
    setFormError(null);
    mutation.mutate(parsed.data);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Payout method"
      description="Where your monthly earnings get sent. We only ever store the last 4 digits of a bank account."
      width={480}
      footer={
        <>
          <button style={btnGhost} onClick={onClose} type="button">Cancel</button>
          <button style={btnPrimary} type="submit" form="payout-method-form" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save method'}
          </button>
        </>
      }
    >
      <form id="payout-method-form" onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Type">
          <select {...register('type')} style={inputStyle}>
            <option value="bank">Bank transfer (ACH)</option>
            <option value="paypal">PayPal</option>
          </select>
        </Field>

        {type === 'bank' ? (
          <>
            <Field label="Bank name" error={errors.bankName?.message}>
              <input {...register('bankName')} placeholder="Chase" style={inputStyle} />
            </Field>
            <Field label="Account holder" error={errors.bankHolder?.message}>
              <input {...register('bankHolder')} placeholder="Marin Okafor" style={inputStyle} />
            </Field>
            <Field label="Account last 4 digits" error={errors.bankLast4?.message}>
              <input
                {...register('bankLast4')}
                placeholder="1234"
                maxLength={4}
                inputMode="numeric"
                style={{ ...inputStyle, fontFamily: 'var(--font-mono)', letterSpacing: '0.2em' }}
              />
            </Field>
          </>
        ) : (
          <Field label="PayPal email" error={errors.paypalEmail?.message}>
            <input {...register('paypalEmail')} placeholder="you@example.com" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </Field>
        )}

        <FormError message={formError} />
      </form>
    </Modal>
  );
}
